import { useToast } from '@cockpitai/hooks/useToast'
import { useSendChatMutation } from '@cockpitai/integration/chat/hooks.ts'
import { KEY_CHAT_HISTORY } from '@cockpitai/integration/chat/keys'
import { TMyPanels } from '@cockpitai/integration/panels/types'
import { messagesStandard } from '@cockpitai/pages/dashboard/Home/useHome'
import { useChatStore } from '@cockpitai/store/chat'
import { sendAnalyticsEvent } from '@cockpitai/utils/analytics'
import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'

export function useChat() {
  const queryClient = useQueryClient()

  const [isAnimated, setIsAnimated] = useState(true)

  const { setOffAnimation } = useChatStore()

  const [_, setSearchParams] = useSearchParams()

  const { showToast } = useToast()

  const {
    setIsStartedChat,
    newMessage,
    messages,
    isLoading: isLoadingChat,
    setIsLoading,
    setChatId,
  } = useChatStore()

  const refAnchor = useRef<HTMLDivElement>(null)

  const { control, handleSubmit, setValue, resetField } = useForm({
    defaultValues: {
      question: '',
    },
  })

  const { mutateAsync } = useSendChatMutation()

  const onSubmit = handleSubmit((values) => {
    if (document.hidden) {
      setIsAnimated(false)
    }

    setIsLoading(true)

    startTransition(() => {
      newMessage({
        type: 'user',
        message: values.question,
        datetime: new Date().toISOString(),
      })
    })

    const chat_id = messages.length > 1 ? messages[1].chatId : undefined

    resetField('question')

    try {
      sendAnalyticsEvent('chat_send_message', {
        message: values.question
      })
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          feature: 'analytics_chat_send_message',
          chat_id: chat_id?.toString() || 'undefined'
        },
        extra: {
          message: 'Error sending analytics event for message sending',
          context: 'useChat_onSubmit_analytics',
          message_content: values.question
        }
      })

      console.warn('Error sending analytics event for message sending', error)
    }

    mutateAsync({ message: values.question, chat_id })
      .then(({ data }) => {
        const { conversation, chat_id } = data

        const {
          answer: { additional_info, id },
        } = conversation

        setChatId(chat_id)

        newMessage({
          type: 'answer',
          message: conversation.answer.message,
          datetime: new Date().toISOString(),
          chatId: chat_id,
          id,
          additional_info,
          animation: isAnimated,
        })

        if (conversation.answer.message) {
          try {
            const answerMessages = Number(localStorage.getItem('answer_messages_count') ?? 0)
            localStorage.setItem('answer_messages_count', String(answerMessages + 1))
          } catch (error) {
            Sentry.captureException(error, {
              tags: {
                feature: 'localStorage_answer_messages_count',
                chat_id: chat_id?.toString() || 'undefined'
              },
              extra: {
                message: 'Error Update Message Accountant on LocalStorage',
                context: 'useChat_onSubmit',
                conversation_message: conversation.answer.message
              }
            })

            console.warn('Error Update Message Accountant', error)
          }
        }

        const hasPanels = additional_info?.filter(
          (item) => item.type === 'panel',
        )

        if (hasPanels?.length) {
          try {
            const panelInfo = hasPanels[0]

            const content = panelInfo.content as TMyPanels

            const filters: {
              affiliateId?: string
              occupationType?: string
              withoutWallet?: 'true'
            } = {}

            if (content?.affiliateId) {
              filters.affiliateId = content?.affiliateId
            }

            if (content.occupationType) {
              filters.occupationType = content?.occupationType.toString()
            }

            if (content.withoutWallet) {
              filters.withoutWallet = content.withoutWallet
            }

            setSearchParams(String(filters))
          } catch (error) {
            Sentry.captureException(error, {
              tags: {
                feature: 'additional_info_panels_processing',
                chat_id: chat_id?.toString() || 'undefined'
              },
              extra: {
                message: 'Error processing additional_info panels',
                context: 'useChat_onSubmit_additional_info',
                additional_info: JSON.stringify(additional_info),
                has_panels_length: hasPanels?.length?.toString() || '0'
              }
            })


            console.warn('Error processing additional_info panels', error)
          }
        }

        const url = new URL(location.href)

        if (!url.pathname.includes(String(chat_id))) {
          const chatUrl = `${url.pathname}/${chat_id}`
          history.pushState({}, '', chatUrl)
        }
      })
      .catch((error) => {
        console.log(error)

        showToast('error', {
          title: 'Ops',
          subtitle: 'Não foi possível enviar sua mensagem, tente mais tarde',
        })
      })
      .finally(() => {
        setIsLoading(false)

        queryClient.invalidateQueries({ queryKey: [KEY_CHAT_HISTORY] })
      })
  })

  const handleSelectOption = (type: 0 | 1 | 2 | 3 | 4) => {
    const message = messagesStandard[type]

    setIsStartedChat(true)

    setValue('question', message.question)

    onSubmit()
  }

  const handleNewMessage = useCallback(() => {
    const inputChat = document.getElementById('chat_input')

    setTimeout(() => {
      if (inputChat) inputChat.focus()

      setIsAnimated(true)
    }, 100)
  }, [])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsAnimated(false)

        setOffAnimation()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [setOffAnimation])

  return {
    control,
    isLoading: isLoadingChat,
    onSubmit,
    refAnchor,
    handleSelectOption,
    handleNewMessage,
    setValue,
  }
}
